// =============================================================================
// Comissão de professores — apuração do mês (Frente 8)
//
// A tela do sistema antigo (comissao-cf.fly.dev), trazida para dentro do Nexa:
// produto e mês, os quatro canais, o total, a comissão e o detalhamento
// lançamento a lançamento para auditoria.
//
// A regra é do produto (058). A vigente, decidida em 07/10/2026, é a do Fly,
// com ADIANTAMENTO: o repasse de M, pago por volta do dia 10, leva o Pix e a
// TMB de M−1 e o cartão e o boleto de M inteiro — o que já caiu e o que ainda
// vai cair. O risco do adiantamento (estorno, cancelamento, parcela que não
// cai) é corrigido no repasse seguinte: cada cálculo refaz o mês anterior com o
// dado do dia e lista a diferença como ACERTO, e o que já foi pago não entra de
// novo. O que o cálculo não alcança entra por ajuste manual, com motivo.
//
// O que muda em relação ao Fly:
//   - não há "Sincronizar": vendas e recebíveis chegam pelos Cron Jobs, e a
//     tela diz de quando é cada fonte;
//   - o mês se FECHA. Fechado, o número não muda mais — o que mudar depois é
//     acerto do mês seguinte; reabrir é do admin, com motivo;
//   - o acerto do mês anterior aparece, lançamento a lançamento, em vez de
//     ficar escondido no total;
//   - os cartões vão em três colunas: em seis, o valor do cartão de crédito
//     ficava cortado em tela de 1440px;
//   - a tabela rola na horizontal no celular, em vez de esconder a coluna do
//     valor.
// =============================================================================

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  api,
  buscarApuracaoComissao,
  buscarHistoricoComissao,
  buscarProdutosComissao,
  FalhaApi,
  type ApuracaoComissao,
  type CanalComissao,
  type ItemComissao,
  type RegraComissao,
} from "../lib/api.ts";
import { competenciaLonga, dataCurta, moeda } from "../lib/formato.ts";
import { paraCentavos } from "../lib/dinheiro.ts";
import { BarraFiltros, Campo, Painel, Pilula, Vazio } from "../componentes/ui.tsx";
import { Icone, type NomeIcone } from "../componentes/Icone.tsx";
import { Modal } from "../componentes/Modal.tsx";

const CANAL: Record<CanalComissao, { rotulo: string; icone: NomeIcone; chip: string }> = {
  pix: { rotulo: "Pix", icone: "qrcode", chip: "bg-ok-bg text-ok-tinta" },
  cartao: { rotulo: "Cartão de crédito", icone: "cartao", chip: "bg-atencao-bg text-laranja-tinta" },
  boleto: { rotulo: "Boleto", icone: "codigoBarras", chip: "bg-[#e6eefb] text-azul-medio" },
  tmb: { rotulo: "TMB", icone: "banco", chip: "bg-[#f1e9fb] text-[#6b3fa0]" },
};
const ORDEM: CanalComissao[] = ["pix", "cartao", "boleto", "tmb"];

const INSTANTE_SP = new Intl.DateTimeFormat("pt-BR", {
  timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
});
const instante = (iso: string | null | undefined) => (iso ? INSTANTE_SP.format(new Date(iso)) : "nunca");
/** "AAAA-MM-DD" → "dd/mm/aaaa", por texto: data de pagamento é rótulo de dia, não instante. */
const dataCompleta = (d: string) => d.split("-").reverse().join("/");
const pct = (pb: number) => `${(pb / 100).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%`;

/** "2026-10" → "2026-09", por texto. */
function mesAntes(mes: string): string {
  const [a = 0, m = 1] = mes.split("-").map(Number);
  return m === 1 ? `${a - 1}-12` : `${a}-${String(m - 1).padStart(2, "0")}`;
}
/** O mês de hoje em São Paulo, "AAAA-MM". */
const mesDeHoje = () => new Intl.DateTimeFormat("en-CA", { timeZone: "America/Sao_Paulo" }).format(new Date()).slice(0, 7);
/**
 * Com adiantamento, o repasse deste mês (em 07/10, o de outubro, pago dia 10).
 * Só o recebido: o mês que acabou de fechar.
 */
const mesPadrao = (regra: RegraComissao | undefined) => (regra === "recebido_no_mes" ? mesAntes(mesDeHoje()) : mesDeHoje());
/** "2026-09" ou "2026-09-01" → "setembro/2026". */
const nomeDoMes = (mes: string) => competenciaLonga(mes.slice(0, 7) + "-01");

/** round(total × pb / 10000), meio centavo para longe do zero — o mesmo da RPC. */
function comissaoDe(total: bigint, pb: number): bigint {
  const abs = total < 0n ? -total : total;
  const c = (abs * BigInt(pb) * 2n + 10_000n) / 20_000n;
  return total < 0n ? -c : c;
}
const soma = (itens: ItemComissao[]) => itens.reduce((s, i) => s + BigInt(i.valor_centavos), 0n);
const PREFIXO_ACERTO = /^ajuste de (\d{4}-\d{2}): /;

/** Os números da apuração, no mesmo formato para a prévia e para o mês fechado. */
function resumo(d: ApuracaoComissao) {
  if (d.fechada) {
    const a = d.apuracao;
    return {
      regra: a.regra,
      canais: {
        pix: { valor_centavos: a.pix_centavos, a_receber_centavos: "0" },
        cartao: { valor_centavos: a.cartao_centavos, a_receber_centavos: "0" },
        boleto: { valor_centavos: a.boleto_centavos, a_receber_centavos: "0" },
        tmb: { valor_centavos: a.tmb_centavos, a_receber_centavos: "0" },
      },
      janelas: a.janelas,
      total: a.total_centavos,
      comissao: a.comissao_centavos,
      percentual_pb: a.percentual_pb,
      itens: a.itens,
      ajusteManual: a.ajuste_centavos,
      motivoAjuste: a.motivo_ajuste,
    };
  }
  const a = d.apuracao;
  return {
    regra: d.regra, canais: a.canais, janelas: a.janelas, total: a.total_centavos, comissao: a.comissao_centavos,
    percentual_pb: a.percentual_pb, itens: a.itens, ajusteManual: "0", motivoAjuste: null as string | null,
  };
}

export function ComissaoApuracao({ papeis }: { papeis: string[] }) {
  const qc = useQueryClient();
  const produtos = useQuery({ queryKey: ["comissao", "produtos"], queryFn: buscarProdutosComissao });
  const ativos = useMemo(() => (produtos.data?.produtos ?? []).filter((p) => p.ativo), [produtos.data]);
  const [produtoId, setProdutoId] = useState("");
  useEffect(() => {
    if (!produtoId && ativos[0]) setProdutoId(ativos[0].id);
  }, [ativos, produtoId]);
  const produto = ativos.find((p) => p.id === produtoId);
  // O mês padrão depende da regra do produto; o que a pessoa escolher vale até ela trocar.
  const [mesEscolhido, setMes] = useState<string | null>(null);
  const mes = mesEscolhido ?? mesPadrao(produto?.regra);

  const apuracao = useQuery({
    queryKey: ["comissao", "apuracao", produtoId, mes],
    queryFn: () => buscarApuracaoComissao(produtoId, mes),
    enabled: !!produto,
    // A prévia lê o Data Gateway: não recalcular a cada foco de janela.
    refetchOnWindowFocus: false,
  });
  const historico = useQuery({
    queryKey: ["comissao", "historico", produtoId],
    queryFn: () => buscarHistoricoComissao(produtoId),
    enabled: !!produto,
  });

  const [confirmarFechar, setConfirmarFechar] = useState(false);
  const [reabrir, setReabrir] = useState(false);
  const invalidar = () => {
    qc.invalidateQueries({ queryKey: ["comissao"] });
  };

  if (produtos.isLoading) return <div className="pagina pt-6"><p className="text-tinta-fraca">Carregando…</p></div>;
  if (produtos.error) return <div className="pagina pt-6"><Erro e={produtos.error} /></div>;
  if (!ativos.length) {
    return (
      <div className="pagina pt-6"><Painel>
        <Vazio titulo="Nenhum produto com comissão cadastrado"
               dica="Cadastre o produto em Comissões → Produtos: as variações dele na Guru, o grupo na TMB, o parceiro, o percentual e a regra." />
      </Painel></div>
    );
  }

  const d = apuracao.data;
  const r = d ? resumo(d) : null;
  const adiantado = r?.regra === "adiantamento";
  const doPeriodo = r?.itens.filter((i) => i.tipo !== "ajuste") ?? [];
  const acertos = r?.itens.filter((i) => i.tipo === "ajuste") ?? [];

  return (
    <div className="pagina space-y-5 pt-6 pb-20">
      <BarraFiltros>
        {/* No celular, o seletor ocupa a linha: o nome do curso é longo e passaria da tela. */}
        <div className="w-full sm:w-auto">
          <Campo rotulo="Produto">
            <select value={produtoId} onChange={(e) => setProdutoId(e.target.value)}
                    className="w-full min-w-0 rounded-lg border border-linha bg-superficie px-3 py-1.5 text-[13px] sm:w-auto sm:min-w-[280px] sm:max-w-[520px]">
              {ativos.map((p) => (
                <option key={p.id} value={p.id}>{p.nome}{p.parceiro ? ` — ${p.parceiro}` : ""} ({pct(p.percentual_pb)})</option>
              ))}
            </select>
          </Campo>
        </div>
        <Campo rotulo={produto?.regra === "recebido_no_mes" ? "Mês" : "Mês do repasse"}>
          <input type="month" value={mes} onChange={(e) => e.target.value && setMes(e.target.value)}
                 className="num rounded-lg border border-linha bg-superficie px-3 py-1.5 text-[13px]" />
        </Campo>
        <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
          {d?.fechada ? (
            <>
              <Pilula tom="ok">Fechada em {instante(d.apuracao.fechada_em)}{d.apuracao.fechada_por_nome ? ` por ${d.apuracao.fechada_por_nome}` : ""}</Pilula>
              {papeis.includes("admin") && (
                <button onClick={() => setReabrir(true)}
                        className="rounded-full border border-linha px-4 py-2 text-[13px] font-medium text-tinta hover:bg-papel">
                  Reabrir
                </button>
              )}
            </>
          ) : d ? (
            <>
              {/* Curta de propósito: a Pílula não quebra linha, e o mês já está escrito logo abaixo. */}
              <Pilula tom="atencao">Prévia · ainda não fechada</Pilula>
              <button onClick={() => setConfirmarFechar(true)}
                      className="rounded-full bg-laranja px-5 py-2 text-[13px] font-bold text-azul hover:brightness-95">
                Fechar apuração
              </button>
            </>
          ) : null}
        </div>
      </BarraFiltros>

      {r && (
        <p className="flex items-start gap-2 text-[12px] text-tinta-fraca">
          <Icone nome="historico" tamanho={14} className="mt-0.5" />
          {adiantado ? (
            <span>
              <strong className="font-semibold text-tinta-media">Repasse de {nomeDoMes(mes)}</strong>: Pix e TMB recebidos
              em {nomeDoMes(mesAntes(mes))}, e cartão e boleto de {nomeDoMes(mes)} inteiro — o que já caiu e o que ainda vai
              cair, adiantado. O que mudar depois de pago (estorno, cancelamento, parcela que não cair) é acertado no repasse seguinte.
            </span>
          ) : (
            <span>
              <strong className="font-semibold text-tinta-media">Tudo o que recebemos em {nomeDoMes(mes)}</strong>
              {r.janelas.pix ? ` (${dataCurta(r.janelas.pix.de)} a ${dataCurta(r.janelas.pix.ate)})` : ""}: Pix, cartão, boleto e TMB. Só entra o que
              já caiu na conta; o que ainda vai cair aparece à parte e entra no mês em que cair.
            </span>
          )}
        </p>
      )}

      {apuracao.isLoading && <p className="text-tinta-fraca">Calculando — a TMB é lida agora do Data Gateway…</p>}
      {apuracao.error && <Erro e={apuracao.error} />}

      {d && !d.fechada && d.avisos.length > 0 && (
        <div className="rounded-xl border border-atencao-linha bg-atencao-bg px-4 py-3 text-[13px] text-atencao-tinta">
          <ul className="list-disc space-y-1 pl-4">{d.avisos.map((a) => <li key={a}>{a}</li>)}</ul>
        </div>
      )}

      {d?.fechada && d.apuracao.observacao && (
        <p className="rounded-xl border border-linha bg-papel px-4 py-3 text-[13px] text-tinta-media">{d.apuracao.observacao}</p>
      )}

      {r && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {ORDEM.map((c, i) => (
              <CartaoCanal key={c} canal={c} indice={i} valor={r.canais[c].valor_centavos} adiantado={adiantado}
                           quantidade={doPeriodo.filter((x) => x.canal === c).length}
                           aReceber={r.canais[c].a_receber_centavos}
                           acerto={soma(acertos.filter((x) => x.canal === c))}
                           janela={c === "pix" ? r.janelas.pix : c === "tmb" ? r.janelas.tmb : r.janelas.recebiveis} />
            ))}
            <CartaoDestaque indice={4} rotulo="Total líquido" valor={moeda(r.total)} icone="carteira"
                            apoio={r.ajusteManual !== "0" ? `Base da comissão, com ajuste manual de ${moeda(r.ajusteManual)}` : "Base de cálculo da comissão"} />
            <CartaoDestaque indice={5} rotulo={`Comissão · ${pct(r.percentual_pb)}`} valor={moeda(r.comissao)}
                            apoio={produto?.parceiro ? `A pagar para ${produto.parceiro}` : "A pagar ao parceiro"} icone="porcentagem" />
          </div>

          <Acerto acertos={acertos} percentual_pb={r.percentual_pb}
                  anterior={d && !d.fechada ? d.anterior : null}
                  ajusteManual={r.ajusteManual} motivoAjuste={r.motivoAjuste} />

          <Detalhamento itens={r.itens} />

          {!d!.fechada && (
            <p className="text-[12px] text-tinta-fraca">
              Vendas da Guru lidas em {instante(d!.linhagem.guru)} · recebíveis da Pagar.me em {instante(d!.linhagem.pagarme)} · TMB {d!.linhagem.tmb}.
            </p>
          )}
        </>
      )}

      {(historico.data?.apuracoes.length ?? 0) > 0 && (
        <Painel>
          <h2 className="font-display text-[16px] font-semibold text-tinta">Meses fechados</h2>
          <div className="thin-scroll mt-3 overflow-x-auto">
            <table className="w-full min-w-[560px] text-[13px]">
              <thead className="text-left text-[11px] tracking-wide text-tinta-fraca uppercase">
                <tr><th className="py-2">Mês</th><th>Fechado em</th><th>Por</th><th className="text-right">Total</th><th className="text-right">Comissão</th><th /></tr>
              </thead>
              <tbody>
                {historico.data!.apuracoes.map((a) => (
                  <tr key={a.id} className={`border-t border-linha align-top ${a.reaberta_em ? "text-tinta-fraca line-through" : ""}`}>
                    <td className="py-2">
                      {nomeDoMes(a.mes)}
                      {a.observacao && <span className="block text-[11px] text-tinta-fraca no-underline">{a.observacao.split(":")[0]}</span>}
                    </td>
                    <td className="num py-2">{instante(a.fechada_em)}</td>
                    <td className="py-2">{a.fechada_por_nome ?? "—"}</td>
                    <td className="num py-2 text-right">{moeda(a.total_centavos)}</td>
                    <td className="num py-2 text-right">{moeda(a.comissao_centavos)}</td>
                    <td className="py-2 pl-3 text-[12px] no-underline">{a.reaberta_em ? `reaberta: ${a.motivo_reabertura}` : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Painel>
      )}

      {confirmarFechar && r && produto && (
        <ModalFechar produtoId={produto.id} mes={mes} total={r.total} percentual_pb={r.percentual_pb}
                     lancamentos={doPeriodo.length} acertos={acertos.length}
                     onFechar={() => setConfirmarFechar(false)} onFeito={() => { setConfirmarFechar(false); invalidar(); }} />
      )}

      {reabrir && d?.fechada && (
        <ModalReabrir apuracaoId={d.apuracao.id} onFechar={() => setReabrir(false)} onFeito={() => { setReabrir(false); invalidar(); }} />
      )}
    </div>
  );
}

function CartaoCanal({ canal, indice, quantidade, valor, aReceber, adiantado, acerto, janela }: {
  canal: CanalComissao; indice: number; quantidade: number; valor: string; aReceber: string;
  adiantado: boolean; acerto: bigint; janela?: { de: string; ate: string };
}) {
  const c = CANAL[canal];
  const unidade = canal === "pix" ? "venda(s)" : canal === "tmb" ? "lançamento(s)" : "parcela(s)";
  return (
    <div className="rise surface-card px-5 py-4" style={{ ["--i" as string]: indice }}>
      <div className="flex items-start justify-between gap-2">
        <span className="text-[12px] font-semibold text-tinta-media">{c.rotulo}</span>
        <span className={`grid size-9 shrink-0 place-items-center rounded-xl ${c.chip}`}><Icone nome={c.icone} tamanho={17} /></span>
      </div>
      <div className="num mt-2 font-display text-[24px] leading-tight font-bold text-tinta">{moeda(valor)}</div>
      <div className="num mt-1 text-[12px] text-tinta-fraca">
        {quantidade} {unidade}{janela ? ` · ${dataCurta(janela.de)} a ${dataCurta(janela.ate)}` : ""}
      </div>
      {aReceber !== "0" && (
        <div className="num mt-1.5 text-[12px] text-atencao-tinta">
          {adiantado
            ? `inclui ${moeda(aReceber)} que ainda vai cair: adiantado`
            : `+ ${moeda(aReceber)} ainda vai cair neste mês: fica fora até cair`}
        </div>
      )}
      {acerto !== 0n && (
        <div className={`num mt-1.5 text-[12px] ${acerto < 0n ? "text-risco-tinta" : "text-ok-tinta"}`}>
          inclui {moeda(acerto.toString())} de acerto do mês anterior
        </div>
      )}
      {canal === "tmb" && (
        <div className="mt-1.5 text-[11px] text-tinta-fraca">Pelo que a TMB informa por integração: renegociações não chegam.</div>
      )}
    </div>
  );
}

function CartaoDestaque({ indice, rotulo, valor, apoio, icone }: {
  indice: number; rotulo: string; valor: string; apoio: string; icone: NomeIcone;
}) {
  return (
    <div className="cf-blueprint rise relative overflow-hidden rounded-xl px-5 py-4 text-white shadow-lg" style={{ ["--i" as string]: indice }}>
      <div aria-hidden className="cf-blueprint-grid absolute inset-0" />
      <div className="relative">
        <div className="flex items-start justify-between gap-2">
          <span className="text-[12px] font-semibold text-azul-clara">{rotulo}</span>
          <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-laranja/15 text-laranja"><Icone nome={icone} tamanho={17} /></span>
        </div>
        <div className="num mt-2 font-display text-[24px] leading-tight font-bold">{valor}</div>
        <div className="mt-1 text-[12px] text-azul-fraca">{apoio}</div>
      </div>
    </div>
  );
}

/**
 * O acerto do fechamento anterior, lançamento a lançamento, e o ajuste manual.
 * Na prévia aparece mesmo sem diferença: "nada mudou" também é informação.
 */
function Acerto({ acertos, percentual_pb, anterior, ajusteManual, motivoAjuste }: {
  acertos: ItemComissao[]; percentual_pb: number;
  anterior: { mes: string; fechada_em: string; observacao: string | null } | null;
  ajusteManual: string; motivoAjuste: string | null;
}) {
  if (!acertos.length && !anterior && ajusteManual === "0") return null;
  const mesAcertado = anterior?.mes ?? PREFIXO_ACERTO.exec(acertos[0]?.referencia ?? "")?.[1];
  const total = soma(acertos);
  return (
    <Painel className="!px-0 !pb-0">
      <div className="px-6">
        <h2 className="font-display text-[16px] font-semibold text-tinta">
          {mesAcertado ? `Acerto do repasse de ${nomeDoMes(mesAcertado)}` : "Ajustes"}
        </h2>
        {anterior && (
          <p className="mt-0.5 text-[12px] text-tinta-fraca">
            Aquele mês, fechado em {instante(anterior.fechada_em)}{/^Importado do sistema antigo/.test(anterior.observacao ?? "") ? " (importado do sistema antigo)" : ""}, refeito com o dado
            de hoje e comparado com o que foi pago. Estorno e cancelamento que chegaram depois descontam; parcela que
            apareceu depois soma. O que já foi pago não entra de novo.
          </p>
        )}
      </div>
      {acertos.length === 0 ? (
        anterior && <p className="flex items-center gap-2 px-6 pt-3 pb-5 text-[13px] text-ok-tinta"><Icone nome="check" tamanho={15} /> Nada mudou desde o pagamento.</p>
      ) : (
        <div className="thin-scroll mt-3 overflow-x-auto">
          <table className="w-full min-w-[620px] text-[13px]">
            <thead className="bg-papel text-left text-[11px] tracking-wide text-tinta-fraca uppercase">
              <tr><th className="px-6 py-2.5">Origem</th><th>Lançamento</th><th>Data</th><th className="px-6 text-right">Diferença</th></tr>
            </thead>
            <tbody>
              {acertos.map((i, n) => (
                <tr key={`${i.referencia}-${n}`} className="border-t border-linha">
                  <td className="px-6 py-2"><span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${CANAL[i.canal].chip}`}>{CANAL[i.canal].rotulo}</span></td>
                  <td className="num text-tinta-media">{i.referencia.replace(PREFIXO_ACERTO, "")}</td>
                  <td className="num">{dataCompleta(i.data)}</td>
                  <td className={`num px-6 text-right ${i.valor_centavos.startsWith("-") ? "text-risco-tinta" : "text-ok-tinta"}`}>{moeda(i.valor_centavos)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-linha font-semibold">
                <td className="px-6 py-2.5" colSpan={3}>Acerto no total · na comissão, {moeda(comissaoDe(total, percentual_pb).toString())}</td>
                <td className={`num px-6 text-right ${total < 0n ? "text-risco-tinta" : ""}`}>{moeda(total.toString())}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      {ajusteManual !== "0" && (
        <div className="border-t border-linha px-6 py-3 text-[13px]">
          <span className="font-semibold text-tinta">Ajuste manual: </span>
          <span className={`num ${ajusteManual.startsWith("-") ? "text-risco-tinta" : ""}`}>{moeda(ajusteManual)}</span>
          {motivoAjuste && <span className="text-tinta-media"> — {motivoAjuste}</span>}
        </div>
      )}
      {acertos.length > 0 && ajusteManual === "0" && <div className="pb-1" />}
    </Painel>
  );
}

type FiltroDetalhe = CanalComissao | "todos" | "ajuste";

function Detalhamento({ itens }: { itens: ItemComissao[] }) {
  const [filtro, setFiltro] = useState<FiltroDetalhe>("todos");
  const visiveis = filtro === "todos" ? itens
    : filtro === "ajuste" ? itens.filter((i) => i.tipo === "ajuste")
    : itens.filter((i) => i.canal === filtro);
  const contagem = (c: CanalComissao) => itens.filter((i) => i.canal === c).length;
  const nAcertos = itens.filter((i) => i.tipo === "ajuste").length;
  return (
    <Painel className="!px-0 !pb-0">
      <div className="flex flex-wrap items-center justify-between gap-3 px-6">
        <div>
          <h2 className="font-display text-[16px] font-semibold text-tinta">Detalhamento para auditoria</h2>
          <p className="text-[12px] text-tinta-fraca">{itens.length} lançamento(s) que compõem o total</p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Chip ativo={filtro === "todos"} onClick={() => setFiltro("todos")}>Todos · {itens.length}</Chip>
          {ORDEM.filter((c) => contagem(c)).map((c) => (
            <Chip key={c} ativo={filtro === c} onClick={() => setFiltro(c)}>{CANAL[c].rotulo} · {contagem(c)}</Chip>
          ))}
          {nAcertos > 0 && <Chip ativo={filtro === "ajuste"} onClick={() => setFiltro("ajuste")}>Acertos · {nAcertos}</Chip>}
        </div>
      </div>
      {itens.length === 0 ? (
        <div className="px-6 pb-6"><Vazio titulo="Nenhum lançamento no período" dica="Confira o mês e se as vendas e os recebíveis do produto já foram carregados." /></div>
      ) : (
        <div className="thin-scroll mt-4 max-h-[560px] overflow-auto">
          <table className="w-full min-w-[620px] text-[13px]">
            <thead className="sticky top-0 bg-papel text-left text-[11px] tracking-wide text-tinta-fraca uppercase">
              <tr><th className="px-6 py-2.5">Origem</th><th>Referência</th><th>Data</th><th className="px-6 text-right">Valor líquido</th></tr>
            </thead>
            <tbody>
              {visiveis.map((i, n) => (
                <tr key={`${i.referencia}-${i.data}-${n}`} className="border-t border-linha">
                  <td className="px-6 py-2"><span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${CANAL[i.canal].chip}`}>{CANAL[i.canal].rotulo}</span></td>
                  <td className="num text-tinta-media">
                    {i.referencia.replace(PREFIXO_ACERTO, "")}
                    {i.tipo === "ajuste" && <Marca>acerto</Marca>}
                    {i.situacao === "a_receber" && <Marca>a receber</Marca>}
                  </td>
                  <td className="num">{dataCompleta(i.data)}</td>
                  <td className={`num px-6 text-right ${i.valor_centavos.startsWith("-") ? "text-risco-tinta" : ""}`}>{moeda(i.valor_centavos)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Painel>
  );
}

function Marca({ children }: { children: ReactNode }) {
  return <span className="ml-2 rounded-full border border-linha px-2 py-px text-[10px] font-semibold tracking-wide text-tinta-fraca uppercase">{children}</span>;
}

function Chip({ ativo, onClick, children }: { ativo: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button onClick={onClick}
            className={`rounded-full border px-3 py-1 text-[12px] font-medium ${ativo ? "border-azul bg-azul text-white" : "border-linha text-tinta-media hover:bg-papel"}`}>
      {children}
    </button>
  );
}

function ModalFechar({ produtoId, mes, total, percentual_pb, lancamentos, acertos, onFechar, onFeito }: {
  produtoId: string; mes: string; total: string; percentual_pb: number; lancamentos: number; acertos: number;
  onFechar: () => void; onFeito: () => void;
}) {
  const [ajusteTexto, setAjusteTexto] = useState("");
  const [motivo, setMotivo] = useState("");
  const ajuste = ajusteTexto.trim() ? paraCentavos(ajusteTexto) : "0";
  const invalido = ajuste === null;
  const final = BigInt(total) + BigInt(ajuste ?? "0");
  const faltaMotivo = !invalido && ajuste !== "0" && !motivo.trim();
  const vazio = lancamentos + acertos === 0 && (ajuste ?? "0") === "0";
  const m = useMutation({
    mutationFn: () => api.post("/api/comissao/apuracao/fechar", {
      produto_id: produtoId, mes, ajuste_centavos: ajuste, motivo_ajuste: motivo.trim() || undefined,
    }),
    onSuccess: onFeito,
  });
  return (
    <Modal titulo={`Fechar ${nomeDoMes(mes)}`} onFechar={onFechar}>
      <p className="text-[13px] text-tinta-media">
        Fechar grava os {lancamentos} lançamento(s){acertos ? ` e os ${acertos} acerto(s) do mês anterior` : ""}, e o total.
        O mês fechado não muda mais: o que mudar depois de pago é acertado automaticamente no fechamento seguinte.
      </p>
      <label className="mt-4 block text-[12px] font-semibold text-tinta-fraca">
        Ajuste manual (opcional)
        <input value={ajusteTexto} onChange={(e) => setAjusteTexto(e.target.value)} inputMode="decimal" placeholder="0,00"
               className="num mt-1 w-full rounded-lg border border-linha px-3 py-2 text-[13px] text-tinta" />
        <span className="mt-1 block font-normal">
          Para o que o cálculo não alcança: um acordo com o parceiro, o mês de uma troca de regra. Negativo desconta.
        </span>
        {invalido && <span className="mt-1 block font-normal text-risco-tinta">Não é um valor: use, por exemplo, -150,00.</span>}
      </label>
      {!invalido && ajuste !== "0" && (
        <label className="mt-3 block text-[12px] font-semibold text-tinta-fraca">
          Motivo do ajuste
          <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={2}
                    className="mt-1 w-full rounded-lg border border-linha px-3 py-2 text-[13px] text-tinta" />
          <span className="mt-1 block font-normal">Fica gravado na apuração e no Histórico.</span>
        </label>
      )}
      <dl className="mt-4 grid grid-cols-2 gap-y-1 text-[13px]">
        <dt className="text-tinta-fraca">Total líquido</dt><dd className="num text-right font-semibold">{moeda(final.toString())}</dd>
        <dt className="text-tinta-fraca">Comissão ({pct(percentual_pb)})</dt><dd className="num text-right font-semibold">{moeda(comissaoDe(final, percentual_pb).toString())}</dd>
      </dl>
      <p className="mt-3 text-[12px] text-tinta-fraca">O servidor recalcula na hora de fechar: se um dado chegou desde a prévia, o fechado já o inclui.</p>
      {m.error && <div className="mt-3"><Erro e={m.error} /></div>}
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onFechar} className="rounded-full border border-linha px-4 py-2 text-[13px]">Cancelar</button>
        <button onClick={() => m.mutate()} disabled={invalido || faltaMotivo || vazio || m.isPending}
                className="rounded-full bg-laranja px-5 py-2 text-[13px] font-bold text-azul disabled:opacity-60">
          {m.isPending ? "Fechando…" : "Fechar o mês"}
        </button>
      </div>
    </Modal>
  );
}

function ModalReabrir({ apuracaoId, onFechar, onFeito }: { apuracaoId: string; onFechar: () => void; onFeito: () => void }) {
  const [motivo, setMotivo] = useState("");
  const m = useMutation({
    mutationFn: () => api.post(`/api/comissao/apuracao/${apuracaoId}/reabrir`, { motivo }),
    onSuccess: onFeito,
  });
  return (
    <Modal titulo="Reabrir a apuração" onFechar={onFechar}>
      <p className="text-[13px] text-tinta-media">
        A apuração fechada continua guardada, marcada como reaberta, e o mês pode ser fechado de novo. Fica no Histórico
        quem reabriu e por quê.
      </p>
      <label className="mt-4 block text-[12px] font-semibold text-tinta-fraca">
        Motivo
        <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3}
                  className="mt-1 w-full rounded-lg border border-linha px-3 py-2 text-[13px] text-tinta" />
      </label>
      {m.error && <div className="mt-3"><Erro e={m.error} /></div>}
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onFechar} className="rounded-full border border-linha px-4 py-2 text-[13px]">Cancelar</button>
        <button onClick={() => m.mutate()} disabled={!motivo.trim() || m.isPending}
                className="rounded-full bg-azul px-5 py-2 text-[13px] font-bold text-white disabled:opacity-50">
          {m.isPending ? "Reabrindo…" : "Reabrir"}
        </button>
      </div>
    </Modal>
  );
}

function Erro({ e }: { e: unknown }) {
  const msg = e instanceof FalhaApi ? e.message : "Não foi possível carregar.";
  return <p className="rounded-lg bg-risco-bg px-4 py-3 text-[13px] text-risco-tinta">{msg}</p>;
}
